import { useState } from 'react'
import { Card, Grid, WideWrap } from 'shared/components/Base/Base'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { ButtonVerticalGroup } from 'shared/components/Button/ButtonVerticalGroup'
import { DropZoneArea } from 'shared/components/DropZoneArea/DropZoneArea'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Back } from 'shared/components/Nav/Back'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { Attachment, isAttachment } from 'shared/models/document'
import { breakpoints } from 'shared/theme/breakpoints'
import { cyanD1, midGray, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { TenstreetIcon, TenstreetSampleBigIcon } from '../../../../components/Icons/Icon'
import { uploadTenstreetDocument } from '../../../../models/employment'
import { User } from '../../../../models/user'
import { useUserProvider } from '../../../Context/UserContext'
import { DriverApplicationContainer } from '../DriverApplication.styles'

const LEARN_MORE_URL =
  'https://support.tenstreet.com/hc/en-us/articles/4719908996891-How-do-I-request-a-copy-of-my-work-history'

interface TenstreetUploadProps {
  nextStep: Handler
  handleRedirect: Handler
  user: User
}

export function TenstreetUpload({ nextStep, user, handleRedirect }: TenstreetUploadProps) {
  const [isTenstreetPreview, setIsTenstreetPreview] = useState(false)
  const [isTenstreetImagePreview, setIsTenstreetImagePreview] = useState(false)
  const [isTenstreetLoading, setIsTenstreetLoading] = useState(false)
  const [file, setFile] = useState<File | Attachment | undefined>()
  const { showBanner, clearUserCache } = useUserProvider()

  const handleUpload = async () => {
    if (!file || isAttachment(file)) return
    setIsTenstreetLoading(true)
    const formData = new FormData()
    formData.append('document', file)
    try {
      await uploadTenstreetDocument(user.id, formData)
      await clearUserCache()
      showBanner('success', {
        message: `Tenstreet application uploaded. Please review and complete the rest of the application.`,
      })
      nextStep()
    } catch (err) {
      showBanner('error', {
        message:
          'Couldn’t upload Tenstreet application. Please try again, or complete your application manually.',
        action: nextStep,
        actionLabel: 'Complete manually',
      })
    }
    setIsTenstreetLoading(false)
  }

  if (isTenstreetImagePreview) {
    return (
      <OuterWrap>
        <Back onClick={() => setIsTenstreetImagePreview(false)} />
        <SampleImgWrap>
          <SampleImg src={TenstreetSampleBigIcon} alt="" />
        </SampleImgWrap>
      </OuterWrap>
    )
  }

  if (isTenstreetPreview) {
    return (
      <OuterWrap>
        <Back onClick={() => setIsTenstreetPreview(false)} />
        <Text variant="header2" align="center">
          Upload Tenstreet resume
        </Text>
        <Text variant="paragraph1" align="center" color={midGray} mt={1}>
          If you have a copy of your Tenstreet resume, upload it here to save time on your application.
        </Text>
        <Box mt={7} />

        <DropZoneArea onChange={setFile} onDelete={() => setFile(undefined)} />
        <Box mt={7} />
        {file && (
          <Button
            variant="primary"
            size="extraLarge"
            isLoading={isTenstreetLoading}
            onClick={handleUpload}
            margin="0 auto 30px"
          >
            Continue
          </Button>
        )}
        <StyledButtonVerticalGroup
          leftButton={
            <Text variant="header4" onClick={() => setIsTenstreetImagePreview(true)} color={royalBlue}>
              View sample
            </Text>
          }
          rightButton={<ExternalLink href={LEARN_MORE_URL}>Learn how to get this document</ExternalLink>}
        />
      </OuterWrap>
    )
  }

  return (
    <DriverApplicationContainer>
      <Text variant="header2" align="center">
        How would you like to complete your application?
      </Text>
      <Box mt={7} />
      <WideWrap width="100%">
        <Grid gap="24px" columns="1fr 1fr" smColumns="1fr" styles="position: relative;">
          <StyledCard align="left" hasSubtleShadow smallerPadding onClick={() => setIsTenstreetPreview(true)}>
            {isTenstreetLoading ? (
              <InlineSpinner mt={8} mb={8} />
            ) : (
              <>
                <Text variant="header3">Upload Tenstreet resume</Text>
                <Text variant="header4" fontColor={cyanD1} mt={1} mb={2}>
                  Faster way to apply
                </Text>
                <img src={TenstreetIcon} alt="" width="108" style={{ marginTop: 'auto' }} />
              </>
            )}
          </StyledCard>

          <StyledCard align="left" hasSubtleShadow smallerPadding onClick={nextStep}>
            <Text variant="header3">Complete application manually</Text>
          </StyledCard>
        </Grid>

        <Box mt={10} smMt={6} />
        <Button variant="text" onClick={handleRedirect} margin="0 auto">
          Go back
        </Button>
      </WideWrap>
    </DriverApplicationContainer>
  )
}

const StyledCard = styled(Card)`
  position: relative;
  min-height: 200px;
  padding: 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StyledButtonVerticalGroup = styled(ButtonVerticalGroup)`
  grid-template-columns: auto 44px auto;
`

const OuterWrap = styled(WideWrap)`
  max-width: 875px;
`

const SampleImgWrap = styled.div`
  text-align: center;

  @media ${breakpoints.sm} {
    grid-column: 1;
    grid-row: 1;
  }
`

const SampleImg = styled.img`
  max-width: 100%;
  width: 756px;
`
